import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { useHistory } from "react-router-dom";
import CustomerSearchSelectAsync from "../../../../../components/CustomerSearchSelectAsync";
import { AuthContext } from "../../../../../helpers/AuthContext";
import { useContext } from "react";

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const STEPS = ["Store Data", "Images & Assets", "Match Products", "Review", "Destination", "Transfer", "Results"];
const PROMOSTORE_DOMAINS = ["promostores.elitecore.ca", "promostores2.elitecore.ca"];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const slugify = (value = "") =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "")
    .slice(0, 80);

const describeMatchReason = (reason, supplierMatch) => {
  const value = String(reason || "").trim();
  if (value === "exact-sku") return "Exact SKU match";
  if (value === "normalized-sku") return "SKU match after normalization";
  if (value === "strong-name") return "Strong product-name match";
  if (value.includes("supplier-mismatch")) return "SKU matched, but supplier differs";
  if (supplierMatch === false) return "Supplier differs from transfer";
  return value ? value.replace(/-/g, " ") : "Native product match";
};

const matchConfidenceLabel = (score) => {
  const value = Number(score || 0);
  if (value >= 95) return "High confidence";
  if (value >= 85) return "Review recommended";
  return "Manual selection";
};

const normalizeAssetPath = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "")
    .replace(/\/{2,}/g, "/");
  if (!normalized) return "";
  const lower = normalized.toLowerCase();
  if (lower.startsWith("images/")) return normalized;
  const marker = lower.indexOf("/images/");
  return marker >= 0 ? normalized.slice(marker + 1) : normalized;
};

const OnlineStoreTransferDialog = ({ open, onClose, instanceUniqueId }) => {
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const inputRef = useRef(null);
  const assetInputRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [assetFiles, setAssetFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [marketScope, setMarketScope] = useState("canada");
  const [productMatches, setProductMatches] = useState({});
  const [matchSuggestions, setMatchSuggestions] = useState({});
  const [matchingAll, setMatchingAll] = useState(false);
  const [customSupplierHints, setCustomSupplierHints] = useState({});
  const [reviewingCustomSuppliers, setReviewingCustomSuppliers] = useState(false);
  const [creatingCustomProducts, setCreatingCustomProducts] = useState({});
  const [customerAssessment, setCustomerAssessment] = useState({
    reviewed: false,
    loading: false,
    candidate: null,
    bestMatch: null,
    matches: [],
    createdCustomer: null,
  });
  const [newCustomerDraft, setNewCustomerDraft] = useState({
    businessName: "",
    emailAddress: "",
    phoneNumber: "",
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [matchSearch, setMatchSearch] = useState({
    externalId: "",
    q: "",
    sourceType: "all",
    loading: false,
    results: [],
  });
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [createdPromoStoreId, setCreatedPromoStoreId] = useState(null);
  const [destination, setDestination] = useState({
    customerId: "",
    domain: "promostores.elitecore.ca",
    title: "",
    pathBase: "",
    storeType: "company_store",
    calculateTaxes: true,
  });

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  }), []);

  const reset = () => {
    setActiveStep(0);
    setFile(null);
    setAssetFiles([]);
    setPreview(null);
    setMarketScope("canada");
    setProductMatches({});
    setMatchSuggestions({});
    setMatchingAll(false);
    setCustomSupplierHints({});
    setReviewingCustomSuppliers(false);
    setCreatingCustomProducts({});
    setCustomerAssessment({
      reviewed: false,
      loading: false,
      candidate: null,
      bestMatch: null,
      matches: [],
      createdCustomer: null,
    });
    setNewCustomerDraft({
      businessName: "",
      emailAddress: "",
      phoneNumber: "",
    });
    setCreatingCustomer(false);
    setMatchSearch({
      externalId: "",
      q: "",
      sourceType: "all",
      loading: false,
      results: [],
    });
    setLoading(false);
    setTransferring(false);
    setError("");
    setResult(null);
    setCreatedPromoStoreId(null);
    setDestination({
      customerId: "",
      domain: "promostores.elitecore.ca",
      title: "",
      pathBase: "",
      storeType: "company_store",
      calculateTaxes: true,
    });
  };

  const close = () => {
    if (loading || transferring) return;
    reset();
    onClose();
  };

  const previewFile = async (selectedFile) => {
    if (!selectedFile) return;
    setError("");
    setLoading(true);
    setFile(selectedFile);
    setAssetFiles([]);
    setProductMatches({});
    setMatchSuggestions({});
    setCustomSupplierHints({});
    setCreatingCustomProducts({});
    setCustomerAssessment({
      reviewed: false,
      loading: false,
      candidate: null,
      bestMatch: null,
      matches: [],
      createdCustomer: null,
    });
    setNewCustomerDraft({
      businessName: "",
      emailAddress: "",
      phoneNumber: "",
    });
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/preview`,
        form,
        { headers: authHeaders },
      );
      const nextPreview = response.data?.preview;
      setPreview(nextPreview);
      setDestination((current) => ({
        ...current,
        customerId: "",
        title: nextPreview?.shop?.name || "",
        pathBase: nextPreview?.shop?.suggestedPathBase || slugify(nextPreview?.shop?.name),
        storeType: nextPreview?.shop?.suggestedStoreType || "company_store",
        calculateTaxes: nextPreview?.shop?.suggestedCalculateTaxes !== false,
      }));
      setActiveStep(1);
    } catch (err) {
      setPreview(null);
      setError(err?.response?.data?.message || "Unable to preview this online store export.");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected) previewFile(selected);
    event.target.value = "";
  };

  const handleAssetFolder = (event) => {
    const selected = Array.from(event.target.files || [])
      .map((asset) => ({
        file: asset,
        path: normalizeAssetPath(asset.webkitRelativePath || asset.name),
      }))
      .filter((entry) => entry.path);
    setAssetFiles(selected);
    setError("");
    event.target.value = "";
  };

  const assetMatch = useMemo(() => {
    const manifest = Array.isArray(preview?.assetManifest) ? preview.assetManifest : [];
    const expectedByKey = new Map(
      manifest.map((entry) => [normalizeAssetPath(entry.localPath).toLowerCase(), entry])
    );
    const selectedByKey = new Map();
    assetFiles.forEach((entry) => {
      const key = normalizeAssetPath(entry.path).toLowerCase();
      if (key && !selectedByKey.has(key)) selectedByKey.set(key, entry);
    });

    const matched = [];
    expectedByKey.forEach((manifestEntry, key) => {
      const selectedEntry = selectedByKey.get(key);
      if (selectedEntry) {
        matched.push({
          ...selectedEntry,
          localPath: normalizeAssetPath(manifestEntry.localPath),
          manifest: manifestEntry,
        });
      }
    });

    const extra = Array.from(selectedByKey.entries())
      .filter(([key]) => !expectedByKey.has(key))
      .map(([, entry]) => entry);

    return {
      expected: manifest.length,
      selected: selectedByKey.size,
      matched,
      missing: Math.max(0, manifest.length - matched.length),
      extra,
    };
  }, [assetFiles, preview]);

  const applyProductMatch = (product, candidate) => {
    if (!product?.externalId || !candidate?.sourceType || !candidate?.productId) return;
    setProductMatches((current) => ({
      ...current,
      [product.externalId]: {
        sourceType: candidate.sourceType,
        productId: String(candidate.productId),
        nativeProductId:
          candidate.sourceType === "custom"
            ? `CUSTOM-${candidate.productId}`
            : String(candidate.productId),
        sourceLabel: candidate.sourceLabel || candidate.sourceType,
        name: candidate.name || "",
        sku: candidate.sku || "",
        supplierName: candidate.supplierName || "",
        imageUrl: candidate.imageUrl || "",
        score: candidate.match?.score ?? candidate.score ?? null,
        reason: candidate.match?.reason ?? candidate.reason ?? null,
        supplierMatch: candidate.match?.supplierMatch ?? null,
        autoMatchedBy: candidate.autoMatchedBy || null,
        createdFromTransfer: candidate.createdFromTransfer === true,
        supplierConfidence: candidate.supplierConfidence ?? null,
        supplierResolutionReason: candidate.supplierResolutionReason || null,
      },
    }));
  };

  const clearProductMatch = (product) => {
    if (!product?.externalId) return;
    setProductMatches((current) => {
      const next = { ...current };
      delete next[product.externalId];
      return next;
    });
  };

  const autoMatchProducts = async () => {
    const products = (preview?.products || []).map((product) => product.matchInput).filter(Boolean);
    if (!products.length || matchingAll) return;
    setMatchingAll(true);
    setError("");
    try {
      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/matching/suggest-batch`,
        { products, marketScope },
        { headers: { ...authHeaders, "Content-Type": "application/json" } },
      );
      const suggestions = response.data?.suggestions || {};
      setMatchSuggestions(suggestions);

      // Only exact / normalized SKU-level confidence is selected automatically.
      // Medium name matches remain visible suggestions for a human to approve.
      setProductMatches((current) => {
        const next = { ...current };
        (preview?.products || []).forEach((product) => {
          if (next[product.externalId]) return;
          const suggestion = suggestions[product.externalId];
          if (suggestion?.sourceType && Number(suggestion?.match?.score || 0) >= 95) {
            next[product.externalId] = {
              sourceType: suggestion.sourceType,
              productId: String(suggestion.productId),
              nativeProductId:
                suggestion.sourceType === "custom"
                  ? `CUSTOM-${suggestion.productId}`
                  : String(suggestion.productId),
              sourceLabel: suggestion.sourceLabel || suggestion.sourceType,
              name: suggestion.name || "",
              sku: suggestion.sku || "",
              supplierName: suggestion.supplierName || "",
              imageUrl: suggestion.imageUrl || "",
              score: suggestion.match?.score ?? null,
              reason: suggestion.match?.reason ?? null,
              supplierMatch: suggestion.match?.supplierMatch ?? null,
              autoMatchedBy: suggestion.autoMatchedBy || null,
            };
          }
        });
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Automatic product matching could not be completed.");
    } finally {
      setMatchingAll(false);
    }
  };

  const reviewCustomSuppliers = async () => {
    const products = (preview?.products || []).map((product) => product.matchInput).filter(Boolean);
    if (!products.length || reviewingCustomSuppliers) return;

    setReviewingCustomSuppliers(true);
    try {
      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/matching/custom-supplier-review`,
        { products },
        { headers: { ...authHeaders, "Content-Type": "application/json" } },
      );
      setCustomSupplierHints(response.data?.suppliers || {});
    } catch (err) {
      console.warn("Unable to review custom-product suppliers for this transfer:", err);
    } finally {
      setReviewingCustomSuppliers(false);
    }
  };

  const createCustomProductFromLine = async (product) => {
    if (!product?.externalId || !file || creatingCustomProducts[product.externalId]) return;

    setCreatingCustomProducts((current) => ({ ...current, [product.externalId]: true }));
    setError("");

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("itemId", product.externalId);

      const productAssets = assetMatch.matched.filter((entry) =>
        String(entry?.manifest?.productExternalId || "") === String(product.externalId) &&
        String(entry?.manifest?.contentType || "").startsWith("image/")
      );

      const assetPaths = [];
      productAssets.forEach((entry) => {
        form.append("assets", entry.file, entry.file.name);
        assetPaths.push(entry.localPath);
      });
      if (assetPaths.length) {
        form.append("assetPaths", JSON.stringify(assetPaths));
      }

      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/matching/create-custom-product`,
        form,
        { headers: authHeaders },
      );

      const match = response.data?.match;
      if (!match?.productId) throw new Error("Custom Product was created but no product ID was returned.");

      applyProductMatch(product, {
        ...match,
        createdFromTransfer: true,
        supplierConfidence: response.data?.supplier?.confidence ?? 100,
        supplierResolutionReason: response.data?.supplier?.reason || null,
        match: {
          score: Number(match.score || 100),
          reason: match.reason || "created-from-transfer",
          supplierMatch: true,
        },
      });

      setMatchSuggestions((current) => {
        const next = { ...current };
        delete next[product.externalId];
        return next;
      });

      if (response.data?.supplier?.Name) {
        setCustomSupplierHints((current) => ({
          ...current,
          [product.supplier]: {
            found: true,
            connected: true,
            confidence: response.data.supplier.confidence || 100,
            reason: response.data.supplier.reason || "created-custom-product",
            supplier: {
              Id: response.data.supplier.Id,
              Name: response.data.supplier.Name,
            },
          },
        }));
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to create a Custom Product from this transfer line."
      );
    } finally {
      setCreatingCustomProducts((current) => ({ ...current, [product.externalId]: false }));
    }
  };

  const openProductMatching = () => {
    setActiveStep(2);
    if (!Object.keys(matchSuggestions).length) {
      autoMatchProducts();
    }
    if (!Object.keys(customSupplierHints).length) {
      reviewCustomSuppliers();
    }
  };

  const verifySuggestedMatch = async (product, suggestion) => {
    if (!product?.externalId || !suggestion?.sourceType) return;

    const query = String(suggestion.sku || product.sku || suggestion.name || product.name || "").trim();
    const sourceType = suggestion.sourceType || "all";

    setMatchSearch({
      externalId: product.externalId,
      q: query,
      sourceType,
      loading: true,
      results: [],
    });
    setError("");

    try {
      const response = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/matching/search`,
        {
          params: {
            q: query,
            sourceType,
            marketScope,
            limit: 15,
          },
          headers: authHeaders,
        },
      );

      const results = response.data?.results || [];
      const suggestedId = String(suggestion.productId || "");
      const orderedResults = [
        ...results.filter((candidate) => String(candidate.productId || "") === suggestedId),
        ...results.filter((candidate) => String(candidate.productId || "") !== suggestedId),
      ];

      setMatchSearch({
        externalId: product.externalId,
        q: query,
        sourceType,
        loading: false,
        results: orderedResults,
      });
    } catch (err) {
      setMatchSearch({
        externalId: product.externalId,
        q: query,
        sourceType,
        loading: false,
        results: [],
      });
      setError(err?.response?.data?.message || "Unable to verify this suggested match.");
    }
  };

  const searchProductMatches = async (product) => {
    if (!product?.externalId) return;
    const query = (
      matchSearch.externalId === product.externalId
        ? matchSearch.q
        : ""
    ).trim() || product.sku || product.name;

    setMatchSearch((current) => ({
      ...current,
      externalId: product.externalId,
      q: query,
      loading: true,
      results: [],
    }));
    setError("");
    try {
      const response = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/matching/search`,
        {
          params: {
            q: query,
            sourceType: matchSearch.sourceType || "all",
            marketScope,
            limit: 15,
          },
          headers: authHeaders,
        },
      );
      setMatchSearch((current) => ({
        ...current,
        externalId: product.externalId,
        q: query,
        loading: false,
        results: response.data?.results || [],
      }));
    } catch (err) {
      setMatchSearch((current) => ({ ...current, loading: false, results: [] }));
      setError(err?.response?.data?.message || "Product search failed.");
    }
  };

  const reviewTransferCustomer = async () => {
    if (!file || customerAssessment.loading) return;

    setCustomerAssessment((current) => ({ ...current, loading: true }));
    setError("");

    try {
      const form = new FormData();
      form.append("file", file);

      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/customer-review`,
        form,
        { headers: authHeaders },
      );

      const candidate = response.data?.candidate || {};
      const bestMatch = response.data?.bestMatch || null;
      const matches = response.data?.matches || [];

      setCustomerAssessment({
        reviewed: true,
        loading: false,
        candidate,
        bestMatch,
        matches,
        createdCustomer: null,
      });

      setNewCustomerDraft({
        businessName: candidate.businessName || preview?.shop?.name || "",
        emailAddress: candidate.emailAddress || "",
        phoneNumber: candidate.phoneNumber || "",
      });

      if (bestMatch?.customer?.id && Number(bestMatch?.match?.score || 0) >= 100) {
        setDestination((current) => ({
          ...current,
          customerId: bestMatch.customer.id,
          title: current.title || bestMatch.customer.businessName || candidate.businessName || "",
        }));
      }
    } catch (err) {
      setCustomerAssessment((current) => ({
        ...current,
        reviewed: true,
        loading: false,
      }));
      setError(err?.response?.data?.message || "Unable to assess the source customer.");
    }
  };

  const openDestination = () => {
    setActiveStep(4);
    if (!customerAssessment.reviewed) {
      reviewTransferCustomer();
    }
  };

  const selectSuggestedCustomer = (entry) => {
    const customer = entry?.customer;
    if (!customer?.id) return;
    setDestination((current) => ({
      ...current,
      customerId: customer.id,
      title: current.title || customer.businessName || "",
    }));
    setCustomerAssessment((current) => ({
      ...current,
      bestMatch: entry,
      createdCustomer: null,
    }));
  };

  const createCustomerFromStore = async () => {
    if (!file || creatingCustomer) return;

    const email = String(newCustomerDraft.emailAddress || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid customer email before creating the customer.");
      return;
    }

    setCreatingCustomer(true);
    setError("");

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("businessName", newCustomerDraft.businessName || preview?.shop?.name || "");
      form.append("emailAddress", email);
      form.append("phoneNumber", newCustomerDraft.phoneNumber || "");
      form.append("defaultCurrency", preview?.shop?.currency || "CAD");

      const response = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/customer-create`,
        form,
        { headers: authHeaders },
      );

      const customer = response.data?.customer;
      if (!customer?.id) throw new Error("Customer was created but no customer ID was returned.");

      setDestination((current) => ({
        ...current,
        customerId: customer.id,
        title: current.title || customer.businessName || preview?.shop?.name || "",
      }));

      setCustomerAssessment((current) => ({
        ...current,
        reviewed: true,
        loading: false,
        bestMatch: {
          customer,
          match: response.data?.match || {
            score: 100,
            reason: response.data?.created ? "created-from-transfer" : "existing-customer",
            confidence: "high",
          },
        },
        createdCustomer: response.data?.created ? customer : null,
      }));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to create a customer from this store."
      );
    } finally {
      setCreatingCustomer(false);
    }
  };

  const canContinueToDestination = Boolean(preview?.summary?.products);
  const canCreateDestination = Boolean(
    destination.customerId &&
    destination.domain &&
    destination.title.trim() &&
    destination.pathBase.trim() &&
    destination.storeType
  );

  const createDestinationStore = async () => {
    const currentAssignedUser = authState?.id
      ? [{
          id: authState.id,
          role: authState.role || "customer",
          first_name: authState.first_name || "",
          last_name: authState.last_name || "",
          profilePicture: authState.profilePicture || "",
        }]
      : [];

    const response = await axios.post(
      `${SERVER}/promostores/create-promostore`,
      {
        domain: destination.domain,
        instanceUniqueId,
        title: destination.title.trim(),
        storeType: destination.storeType,
        calculateTaxes: destination.calculateTaxes,
        attachedCustomers: [destination.customerId],
        AssignedTo: currentAssignedUser,
        pathBase: slugify(destination.pathBase),
        Currency: preview?.shop?.currency || "CAD",
      },
      {
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
      },
    );

    const promoStoreId = response.data?.promoStoreId || response.data?.promostore?.id;
    if (!promoStoreId) throw new Error("PromoStore was created but no PromoStore ID was returned.");
    setCreatedPromoStoreId(Number(promoStoreId));
    return Number(promoStoreId);
  };

  const commitTransfer = async (promoStoreId) => {
    const form = new FormData();
    form.append("file", file);
    form.append("promoStoreId", String(promoStoreId));
    const assetPaths = [];
    assetMatch.matched.forEach((entry) => {
      form.append("assets", entry.file, entry.file.name);
      assetPaths.push(entry.localPath);
    });
    if (assetPaths.length) form.append("assetPaths", JSON.stringify(assetPaths));
    if (Object.keys(productMatches).length) {
      form.append("productMatches", JSON.stringify(productMatches));
    }

    const response = await axios.post(
      `${SERVER}/api/instances/${instanceUniqueId}/tools/transfer-online-store/commit`,
      form,
      { headers: authHeaders },
    );
    return response.data?.result;
  };

  const runTransfer = async () => {
    if (!file || !preview) return;
    setError("");
    setTransferring(true);
    setActiveStep(5);
    let targetPromoStoreId = createdPromoStoreId;

    try {
      targetPromoStoreId = targetPromoStoreId || await createDestinationStore();
      const transferResult = await commitTransfer(targetPromoStoreId);
      setResult(transferResult);
      setActiveStep(6);
    } catch (err) {
      if (targetPromoStoreId) {
        setCreatedPromoStoreId(Number(targetPromoStoreId));
        setActiveStep(5);
      } else {
        // Store creation validation/path errors should return the user to the
        // destination form instead of trapping them on the progress screen.
        setActiveStep(4);
      }
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "The transfer could not be completed."
      );
    } finally {
      setTransferring(false);
    }
  };

  const retryImport = async () => {
    if (!createdPromoStoreId) return runTransfer();
    setError("");
    setTransferring(true);
    setActiveStep(5);
    try {
      const transferResult = await commitTransfer(createdPromoStoreId);
      setResult(transferResult);
      setActiveStep(6);
    } catch (err) {
      setError(err?.response?.data?.message || "The product import could not be completed.");
    } finally {
      setTransferring(false);
    }
  };

  const openStore = () => {
    const id = result?.promoStoreId || createdPromoStoreId;
    if (!id) return;
    close();
    history.push(`/instance/${instanceUniqueId}/sales/promostores/${id}`);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
        <StorefrontOutlinedIcon color="primary" />
        Transfer an Online Store
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 560 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {(loading || transferring) && <LinearProgress sx={{ mb: 2 }} />}

        {activeStep === 0 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity="info">
              First release support is intentionally limited to the Elite commonsku shop JSON export format. The file is validated before any PromoStore is created.
            </Alert>
            <Paper
              variant="outlined"
              onClick={() => inputRef.current?.click()}
              sx={{
                p: 5,
                textAlign: "center",
                cursor: loading ? "default" : "pointer",
                borderStyle: "dashed",
                bgcolor: "grey.50",
              }}
            >
              <UploadFileOutlinedIcon color="primary" sx={{ fontSize: 46, mb: 1 }} />
              <Typography variant="h6">Upload online store JSON</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Product pricing, internal costs, variants, decorations, image URLs and store metadata will be analyzed.
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }} disabled={loading}>
                Choose JSON File
              </Button>
              <input ref={inputRef} hidden type="file" accept=".json,application/json" onChange={handleFile} />
            </Paper>
          </Box>
        )}

        {activeStep === 1 && preview && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity="info">
              Optional: add the accompanying images folder now. Elite Core matches files to the JSON by local_path. You can skip this step and use source URLs where available.
            </Alert>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
              {[
                ["Bundled files expected", preview.summary?.bundledAssetsExpected],
                ["Image files", preview.summary?.bundledImageAssets],
                ["PDF resources", preview.summary?.bundledPdfAssets],
                ["Remote-only assets", preview.summary?.remoteOnlyAssets],
              ].map(([label, value]) => (
                <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6">{formatNumber(value)}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              ))}
            </Box>

            <Paper
              variant="outlined"
              onClick={() => assetInputRef.current?.click()}
              sx={{
                p: 4,
                textAlign: "center",
                cursor: loading ? "default" : "pointer",
                borderStyle: "dashed",
                bgcolor: "grey.50",
              }}
            >
              <FolderOpenOutlinedIcon color="primary" sx={{ fontSize: 46, mb: 1 }} />
              <Typography variant="h6">
                {assetFiles.length ? "Choose a different images folder" : "Add images folder"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select the folder containing the paths referenced by the export. Extra files are ignored.
              </Typography>
              <Button variant="outlined" sx={{ mt: 2 }}>Choose Folder</Button>
              <input
                ref={assetInputRef}
                hidden
                type="file"
                multiple
                webkitdirectory=""
                directory=""
                accept="image/*,.pdf,application/pdf"
                onChange={handleAssetFolder}
              />
            </Paper>

            {assetFiles.length > 0 && (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
                  {[
                    ["Selected", assetMatch.selected],
                    ["Matched", assetMatch.matched.length],
                    ["Missing", assetMatch.missing],
                    ["Extra ignored", assetMatch.extra.length],
                  ].map(([label, value]) => (
                    <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="h6">{formatNumber(value)}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Paper>
                  ))}
                </Box>
                {assetMatch.missing > 0 && (
                  <Alert severity="warning">
                    {assetMatch.missing} referenced bundled file(s) were not found. Core will fall back to source URLs where available.
                  </Alert>
                )}
                {assetMatch.matched.length > 0 && (
                  <Alert severity="success">
                    {assetMatch.matched.length} referenced asset(s) are ready to copy into Elite Core storage during transfer.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        {activeStep === 2 && preview && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity="info">
              Match transferred lines to native Elite Core products when possible. Matching enriches product identity, variants, imprinting, supplier data, images and missing source costs. Transfer pricing, smart-pricing rules and bundled assets remain authoritative.
            </Alert>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>ASI market</InputLabel>
                <Select
                  label="ASI market"
                  value={marketScope}
                  onChange={(event) => {
                    setMarketScope(event.target.value);
                    setMatchSuggestions({});
                    setProductMatches((current) =>
                      Object.fromEntries(
                        Object.entries(current).filter(([, match]) => match?.sourceType !== "asi")
                      )
                    );
                  }}
                >
                  <MenuItem value="canada">Canada</MenuItem>
                  <MenuItem value="global">Global</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" disabled={matchingAll} onClick={autoMatchProducts}>
                {matchingAll ? "Matching…" : "Auto-match all"}
              </Button>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`${Object.keys(productMatches).length} matched`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${Math.max((preview.products || []).length - Object.keys(productMatches).length, 0)} transfer-only`}
              />
            </Box>

            {matchingAll && <LinearProgress />}

            <Box sx={{ display: "grid", gap: 1 }}>
              {(preview.products || []).map((product) => {
                const selected = productMatches[product.externalId];
                const suggestion = matchSuggestions[product.externalId];
                const supplierHint = customSupplierHints[product.supplier] || null;
                const isPsProduct = String(product.supplierSource || "").toLowerCase() === "ps-products";
                const canSeedCustom = !selected && isPsProduct && supplierHint?.found;
                const creatingCustom = Boolean(creatingCustomProducts[product.externalId]);
                const isSearching = matchSearch.externalId === product.externalId;
                return (
                  <Paper key={product.externalId} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku || "No SKU"} · {product.supplier || "Unknown supplier"}
                          {product.supplierSource ? ` · ${product.supplierSource}` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
                        {selected?.imageUrl && (
                          <Box
                            component="img"
                            src={selected.imageUrl}
                            alt=""
                            sx={{ width: 34, height: 34, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
                          />
                        )}
                        {selected ? (
                          <>
                            <Chip
                              size="small"
                              color="success"
                              label={`${selected.sourceLabel || selected.sourceType}: ${selected.sku || selected.name || selected.productId}`}
                            />
                            <Button size="small" onClick={() => clearProductMatch(product)}>Clear</Button>
                          </>
                        ) : suggestion?.sourceType ? (
                          <>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`Suggested: ${suggestion.sourceLabel || suggestion.sourceType} · ${suggestion.sku || suggestion.name}`}
                            />
                            <Button size="small" onClick={() => verifySuggestedMatch(product, suggestion)}>
                              Verify Match
                            </Button>
                            <Button size="small" onClick={() => applyProductMatch(product, suggestion)}>
                              Use Match
                            </Button>
                          </>
                        ) : (
                          <Chip size="small" variant="outlined" label="Use transfer data" />
                        )}
                        {canSeedCustom && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={creatingCustom}
                            onClick={() => createCustomProductFromLine(product)}
                          >
                            {creatingCustom ? "Creating…" : "Create Custom Product"}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setMatchSearch((current) => (
                            current.externalId === product.externalId
                              ? {
                                  externalId: "",
                                  q: "",
                                  sourceType: "all",
                                  loading: false,
                                  results: [],
                                }
                              : {
                                  externalId: product.externalId,
                                  q: product.sku || product.name || "",
                                  sourceType: "all",
                                  loading: false,
                                  results: [],
                                }
                          ))}
                        >
                          {isSearching ? "Hide Search" : "Search / Rematch"}
                        </Button>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 0.75 }}>
                      {selected ? (
                        <Typography variant="caption" color="success.main">
                          Selected native match · {matchConfidenceLabel(selected.score)}
                          {selected.score ? ` (${selected.score}/100)` : ""}
                          {" · "}{describeMatchReason(selected.reason, selected.supplierMatch)}
                          {selected.supplierName ? ` · ${selected.supplierName}` : ""}
                          {selected.autoMatchedBy === "source-external-product-id" ? " · matched from source product ID" : ""}
                          {selected.productId ? ` · ${selected.sourceType === "custom" ? "CUSTOM-" : ""}${selected.productId}` : ""}
                          {selected.createdFromTransfer ? " · created from this transfer" : ""}
                          {selected.supplierConfidence ? ` · supplier ${selected.supplierConfidence}/100` : ""}
                        </Typography>
                      ) : suggestion?.sourceType ? (
                        <Typography variant="caption" color="warning.main">
                          Confirmation required · {matchConfidenceLabel(suggestion?.match?.score)}
                          {suggestion?.match?.score ? ` (${suggestion.match.score}/100)` : ""}
                          {" · "}{describeMatchReason(suggestion?.match?.reason, suggestion?.match?.supplierMatch)}
                          {suggestion.supplierName ? ` · ${suggestion.supplierName}` : ""}
                          {suggestion.autoMatchedBy === "source-external-product-id" ? " · found from source product ID" : ""}
                        </Typography>
                      ) : (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            No native match reached the automatic suggestion threshold. This product will import from the transfer data unless you search and select a match manually.
                          </Typography>
                          {isPsProduct && supplierHint?.found && (
                            <Typography variant="caption" color="primary.main" sx={{ display: "block", mt: 0.25 }}>
                              Likely custom-catalog product · You already have this supplier in Core: {supplierHint.supplier?.Name || product.supplier}
                              {supplierHint.confidence ? ` (${supplierHint.confidence}/100)` : ""}
                              {supplierHint.connected
                                ? " · already used for custom products"
                                : " · it will be linked automatically when you create this custom product"}
                            </Typography>
                          )}
                          {isPsProduct && supplierHint && !supplierHint.found && (
                            <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.25 }}>
                              Likely custom-catalog product · supplier could not be matched confidently yet.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {isSearching && (
                      <Box sx={{ mt: 1.5, p: 1.25, bgcolor: "grey.50", borderRadius: 1.5 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 160px auto", gap: 1 }}>
                          <TextField
                            size="small"
                            label="Search SKU, product name or supplier"
                            value={matchSearch.q}
                            onChange={(event) => setMatchSearch((current) => ({ ...current, q: event.target.value }))}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                searchProductMatches(product);
                              }
                            }}
                          />
                          <FormControl size="small">
                            <InputLabel>Source</InputLabel>
                            <Select
                              label="Source"
                              value={matchSearch.sourceType}
                              onChange={(event) => setMatchSearch((current) => ({ ...current, sourceType: event.target.value }))}
                            >
                              <MenuItem value="all">All sources</MenuItem>
                              <MenuItem value="asi">ASI</MenuItem>
                              <MenuItem value="custom">Custom Core</MenuItem>
                              <MenuItem value="catalog">PromoWorld</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            disabled={matchSearch.loading}
                            onClick={() => searchProductMatches(product)}
                          >
                            Search
                          </Button>
                        </Box>

                        {matchSearch.loading && <LinearProgress sx={{ mt: 1 }} />}

                        {Array.isArray(matchSearch.results) && matchSearch.results.length > 0 && (
                          <Box sx={{ display: "grid", gap: 0.75, mt: 1 }}>
                            {matchSearch.results.map((candidate) => (
                              <Box
                                key={`${candidate.sourceType}:${candidate.productId}`}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1.5,
                                  p: 1,
                                  bgcolor: "background.paper",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {candidate.name || "Unnamed product"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    {candidate.sourceLabel || candidate.sourceType} · {candidate.sku || "No SKU"} · {candidate.supplierName || "Unknown supplier"}
                                  </Typography>
                                  <Typography variant="caption" color="text.disabled">
                                    Product ID: {candidate.productId || "—"}
                                    {suggestion?.productId && String(candidate.productId) === String(suggestion.productId)
                                      ? " · Suggested match"
                                      : ""}
                                  </Typography>
                                </Box>
                                <Button size="small" onClick={() => applyProductMatch(product, candidate)}>
                                  Select
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}

        {activeStep === 3 && preview && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography variant="h6">{preview.shop?.name || "Imported store"}</Typography>
              <Typography variant="body2" color="text.secondary">
                commonsku shop #{preview.shop?.shopNumber || "—"} · {preview.shop?.currency || "CAD"} · exported {preview.exportedAt || "—"}
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 1 }}>
              {[
                ["Products", preview.summary?.products],
                ["Pricing rows", preview.summary?.pricingRows],
                ["Variants", preview.summary?.variants],
                ["Decoration locations", preview.summary?.decorationLocations],
                ["Products with images", preview.summary?.productsWithImages],
              ].map(([label, value]) => (
                <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6">{formatNumber(value)}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              ))}
            </Box>

            {preview.shop?.introduction && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Source store introduction</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{preview.shop.introduction}</Typography>
              </Paper>
            )}

            {(preview.warnings || []).map((warning) => (
              <Alert severity="warning" key={warning}>{warning}</Alert>
            ))}

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Native product matching</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip size="small" color="success" variant="outlined" label={`${Object.keys(productMatches).length} matched`} />
                <Chip size="small" variant="outlined" label={`${Math.max((preview.products || []).length - Object.keys(productMatches).length, 0)} transfer-only`} />
                <Chip size="small" variant="outlined" label={`ASI market: ${marketScope === "canada" ? "Canada" : "Global"}`} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                Matching enriches the native product structure; migrated storefront prices, smart-pricing rules and bundled assets remain unchanged.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Bundled asset package</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip size="small" label={`${assetMatch.matched.length} matched`} color={assetMatch.matched.length ? "success" : "default"} variant="outlined" />
                <Chip size="small" label={`${assetMatch.missing} missing`} color={assetMatch.missing ? "warning" : "default"} variant="outlined" />
                <Chip size="small" label={`${preview.summary?.remoteOnlyAssets || 0} remote-only`} variant="outlined" />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Price tiers</TableCell>
                    <TableCell align="right">Variants</TableCell>
                    <TableCell align="right">Pricing rows</TableCell>
                    <TableCell align="right">Images</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(preview.products || []).map((product) => (
                    <TableRow key={product.externalId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{product.sku || "No SKU"}</Typography>
                      </TableCell>
                      <TableCell>{product.supplier || "—"}</TableCell>
                      <TableCell align="right">{product.priceTiers}</TableCell>
                      <TableCell align="right">{product.variants}</TableCell>
                      <TableCell align="right">{product.pricingRows}</TableCell>
                      <TableCell align="right">{product.images}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {activeStep === 4 && preview && (
          <Box sx={{ display: "grid", gap: 2, maxWidth: 760, mx: "auto" }}>
            <Alert severity="info">
              Elite Core will create a normal native PromoStore first, then load this catalog into it. The store remains editable through the regular PromoStore product tools afterward.
            </Alert>

            {customerAssessment.loading && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Checking for an existing customer…</Typography>
                <LinearProgress sx={{ mt: 1 }} />
              </Paper>
            )}

            {!customerAssessment.loading && customerAssessment.reviewed && customerAssessment.bestMatch?.customer && (
              <Alert
                severity={Number(customerAssessment.bestMatch?.match?.score || 0) >= 100 ? "success" : "warning"}
                action={Number(customerAssessment.bestMatch?.match?.score || 0) < 100 ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => selectSuggestedCustomer(customerAssessment.bestMatch)}
                  >
                    Use Customer
                  </Button>
                ) : undefined}
              >
                {customerAssessment.createdCustomer
                  ? `Customer created and attached: ${customerAssessment.createdCustomer.businessName}`
                  : Number(customerAssessment.bestMatch?.match?.score || 0) >= 100
                    ? `Existing customer found and selected: ${customerAssessment.bestMatch.customer.businessName} (100/100)`
                    : `Possible customer match: ${customerAssessment.bestMatch.customer.businessName} (${customerAssessment.bestMatch.match.score}/100). Review before using.`}
              </Alert>
            )}

            {!customerAssessment.loading &&
              customerAssessment.reviewed &&
              !destination.customerId &&
              (!customerAssessment.bestMatch?.customer ||
                Number(customerAssessment.bestMatch?.match?.score || 0) < 100) && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {customerAssessment.bestMatch?.customer
                      ? "Create a new customer instead"
                      : "No existing customer matched this store"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    Create the customer directly from the source store. Core will use every reliable customer field supplied by the export and attach the new customer to this PromoStore.
                  </Typography>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <TextField
                      label="Customer / company name"
                      value={newCustomerDraft.businessName}
                      onChange={(event) => setNewCustomerDraft((current) => ({ ...current, businessName: event.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Customer email"
                      type="email"
                      required
                      value={newCustomerDraft.emailAddress}
                      onChange={(event) => setNewCustomerDraft((current) => ({ ...current, emailAddress: event.target.value }))}
                      helperText={
                        customerAssessment.candidate?.emailAddress
                          ? "Provided by the source store"
                          : "Required because this export does not include a customer email"
                      }
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={newCustomerDraft.phoneNumber}
                      onChange={(event) => setNewCustomerDraft((current) => ({ ...current, phoneNumber: event.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Currency"
                      value={customerAssessment.candidate?.defaultCurrency || preview?.shop?.currency || "CAD"}
                      disabled
                      fullWidth
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    sx={{ mt: 1.5 }}
                    disabled={
                      creatingCustomer ||
                      !String(newCustomerDraft.businessName || "").trim() ||
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(newCustomerDraft.emailAddress || "").trim())
                    }
                    onClick={createCustomerFromStore}
                  >
                    {creatingCustomer ? "Creating Customer…" : "Create Customer & Use"}
                  </Button>
                </Paper>
              )}

            <CustomerSearchSelectAsync
              instanceUniqueId={instanceUniqueId}
              value={destination.customerId}
              onChange={(customerId) => setDestination((current) => ({ ...current, customerId: customerId || "" }))}
              onSelectCustomer={(customer) => {
                if (!customer) return;
                const customerName = customer.businessName || [customer.firstName, customer.lastName].filter(Boolean).join(" ");
                setDestination((current) => ({
                  ...current,
                  customerId: customer.id || current.customerId,
                  title: current.title || customerName || "",
                }));
              }}
              label={customerAssessment.reviewed && !destination.customerId
                ? "Or select an existing customer"
                : "Customer Account"}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="PromoStore name"
                value={destination.title}
                onChange={(event) => setDestination((current) => ({ ...current, title: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Store path"
                value={destination.pathBase}
                onChange={(event) => setDestination((current) => ({ ...current, pathBase: slugify(event.target.value) }))}
                helperText={destination.pathBase ? `/${slugify(destination.pathBase)}` : "Required"}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>PromoStore domain</InputLabel>
                <Select
                  label="PromoStore domain"
                  value={destination.domain}
                  onChange={(event) => setDestination((current) => ({ ...current, domain: event.target.value }))}
                >
                  {PROMOSTORE_DOMAINS.map((domain) => <MenuItem key={domain} value={domain}>{domain}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Store type</InputLabel>
                <Select
                  label="Store type"
                  value={destination.storeType}
                  onChange={(event) => setDestination((current) => ({ ...current, storeType: event.target.value }))}
                >
                  <MenuItem value="company_store">Company Store</MenuItem>
                  <MenuItem value="program_store">Program Store</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={destination.calculateTaxes}
                  onChange={(event) => setDestination((current) => ({ ...current, calculateTaxes: event.target.checked }))}
                />
              )}
              label="Calculate taxes in the new PromoStore"
            />

            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>What will be preserved</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {["Sell-price tiers", "Internal costs", "Variants", "Size upcharges", "Decoration metadata", "Product images", "Store colour", "Source metadata"].map((label) => (
                  <Chip size="small" key={label} label={label} variant="outlined" />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {activeStep === 5 && (
          <Box sx={{ display: "grid", placeItems: "center", minHeight: 360, textAlign: "center" }}>
            <Box>
              <StorefrontOutlinedIcon color="primary" sx={{ fontSize: 54 }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                {transferring ? "Creating and loading PromoStore…" : "Transfer needs attention"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 560 }}>
                {createdPromoStoreId
                  ? "The destination PromoStore already exists. You can retry the catalog import without creating another store."
                  : "Elite Core will create the native PromoStore and then import the validated catalog."}
              </Typography>
              {!transferring && createdPromoStoreId && (
                <Button variant="contained" sx={{ mt: 2 }} onClick={retryImport}>Retry Catalog Import</Button>
              )}
            </Box>
          </Box>
        )}

        {activeStep === 6 && result && (
          <Box sx={{ display: "grid", gap: 2, maxWidth: 760, mx: "auto" }}>
            <Alert severity="success">
              {result.message || "Online store transferred successfully."}
            </Alert>
            <Typography variant="h6">{result.sourceShopName || preview?.shop?.name}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
              {[
                ["Products", result.products],
                ["Created", result.created],
                ["Updated", result.updated],
                ["Pricing rows", result.pricingRows],
              ].map(([label, value]) => (
                <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="h6">{formatNumber(value)}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              ))}
            </Box>
            {result.assets && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Transferred assets</Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  <Chip size="small" label={`${formatNumber(result.assets.uploaded)} uploaded`} color="success" variant="outlined" />
                  <Chip size="small" label={`${formatNumber(result.assets.missing)} missing`} variant="outlined" />
                  <Chip size="small" label={`${formatNumber(result.assets.remoteOnly)} remote-only`} variant="outlined" />
                  {result.assets.failed > 0 && <Chip size="small" label={`${formatNumber(result.assets.failed)} failed`} color="warning" variant="outlined" />}
                </Box>
              </Paper>
            )}
            <Alert severity="info">
              The imported catalog is now stored in Elite Core. Open the PromoStore to review products and initiate the normal WordPress sync when ready.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep === 0 && <Button onClick={close}>Cancel</Button>}

        {activeStep === 1 && (
          <>
            <Button onClick={() => {
              setActiveStep(0);
              setPreview(null);
              setFile(null);
              setAssetFiles([]);
              setProductMatches({});
              setMatchSuggestions({});
              setCustomSupplierHints({});
              setCreatingCustomProducts({});
              setCustomerAssessment({
                reviewed: false,
                loading: false,
                candidate: null,
                bestMatch: null,
                matches: [],
                createdCustomer: null,
              });
              setNewCustomerDraft({ businessName: "", emailAddress: "", phoneNumber: "" });
            }}>
              Choose Different JSON
            </Button>
            <Button variant="contained" onClick={openProductMatching}>
              {assetFiles.length ? "Match Products" : "Skip Assets & Match Products"}
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)}>Back to Assets</Button>
            <Button variant="contained" onClick={() => setActiveStep(3)}>
              Review Transfer
            </Button>
          </>
        )}

        {activeStep === 3 && (
          <>
            <Button onClick={() => setActiveStep(2)}>Back to Matching</Button>
            <Button variant="contained" disabled={!canContinueToDestination} onClick={openDestination}>
              Configure Destination
            </Button>
          </>
        )}

        {activeStep === 4 && (
          <>
            <Button onClick={() => setActiveStep(3)}>Back</Button>
            <Button variant="contained" disabled={!canCreateDestination || transferring} onClick={runTransfer}>
              Create PromoStore & Transfer
            </Button>
          </>
        )}

        {activeStep === 5 && (
          <Button onClick={close} disabled={transferring}>Close</Button>
        )}

        {activeStep === 6 && (
          <>
            <Button onClick={close}>Close</Button>
            <Button variant="contained" onClick={openStore}>Open PromoStore</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnlineStoreTransferDialog;
